import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { getStaffList } from "api/staff";
import CustomReactSelect from "components/CustomReactSelect";
import BaseTable from "components/DataTable/BaseTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import useFilterStore from "store/useFilterStore";
import { formatDateTime, getRoleFilters, getStaffStatusFilters, STATUS_ACTIVE, STATUS_INACTIVE } from "utils/helper";

const TABLE_COLUMNS = [
    { id: "full_name", Header: "Full name", accessor: "fullname", align: "left", sortable: true },
    { id: "role", Header: "Role", accessor: "role", align: "left", sortable: true },
    { id: "start_working_date", Header: "Start Working Date", accessor: "workDate", align: "left" },
    { id: "created_at", Header: "Created At", accessor: "created_at", align: "left", sortable: true },
    { id: "status", Header: "Status", accessor: "status", align: "left" },
];

export const parentRoute = '/staffs';

const ROLE_FILTERS = getRoleFilters()
const STATUS_FILTERS = getStaffStatusFilters()


function StaffTable() {
    const navigate = useNavigate();
    const location = useLocation();
    const currentPath = window.location.pathname;
    const { getFilters, setFilters } = useFilterStore();
    const [refreshKey] = useState(0);
    const savedState = location.state || { sort: "created_at", dir: "desc" };

    // Multiple filter states
    const [selectedRole, setSelectedRole] = useState("");
    const [status, setStatus] = useState("");

    // Restore custom filters (role, status) from store on mount
    useEffect(() => {
        const record = getFilters(currentPath);
        if (record?.customParams) {
            if (record.customParams.selectedRole !== undefined) setSelectedRole(record.customParams.selectedRole);
            if (record.customParams.status !== undefined) setStatus(record.customParams.status);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist custom filters whenever they change (merge with existing baseTable state)
    useEffect(() => {
        const record = getFilters(currentPath);
        setFilters(currentPath, {
            baseTable: record.baseTable, // preserve the table paging/sort managed by BaseTable
            customParams: { selectedRole, status }
        });
    }, [selectedRole, status, currentPath]);


    const createTableRow = useCallback((item) => ({
        fullname: <MDTypography component="div" variant="standard" color="text">{item.full_name}</MDTypography>,
        role: <MDTypography component="div" variant="standard" color="text">{item.role?.name}</MDTypography>,
        workDate: <MDTypography component="div" variant="standard" color="text">
            {item.start_working_date || '-'}
        </MDTypography>,
        created_at: <MDTypography component="div" variant="standard" color="text">
            {formatDateTime(item.created_at)}
        </MDTypography>,
        status: <MDTypography component="div" variant="standard" color={item.status?.id === STATUS_ACTIVE ? "success" : item.status?.id === STATUS_INACTIVE ? "error" : "warning"}>
            {item.status?.name}
        </MDTypography>
    }), []);

    const fetchWithFilters = useCallback(async (params = {}) => {
        const filterParams = {
            ...params,
            ...(selectedRole ? { roles: selectedRole } : {}),
            ...(status ? { status } : {}),
        };
        return getStaffList(filterParams);
    }, [selectedRole, status]);

    const filters = (
        <>
            {/* Role Filter */}
            <MDBox sx={{ flex: 1, minWidth: 120 }}>
                <CustomReactSelect
                    label="Role"
                    name="role"
                    placeholder="All Roles"
                    value={ROLE_FILTERS.find(role => role.value === selectedRole)}
                    onChange={(selectedOption) => setSelectedRole(selectedOption?.value)}
                    options={ROLE_FILTERS}
                    isClearable
                />
            </MDBox>

            {/* Experience Filter */}
            <MDBox sx={{ flex: 1, minWidth: 140 }}>
                <CustomReactSelect
                    label="Status"
                    name="status"
                    placeholder="All Status"
                    value={STATUS_FILTERS.find(exp => exp.value === status)}
                    onChange={(selectedOption) => setStatus(selectedOption?.value)}
                    options={STATUS_FILTERS}
                    isClearable
                />
            </MDBox>
        </>
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <BaseTable
                key={refreshKey}
                title="Staffs Management"
                columns={TABLE_COLUMNS}
                fetchData={fetchWithFilters}
                defaultState={savedState}
                createTableRow={createTableRow}
                showAddButton
                addButtonText="Add Staff"
                onAddClick={() => navigate(`${parentRoute}/add-staff`, { state: savedState })}
                showSearch
                searchPlaceholder="Search staff name..."
                additionalFilters={filters}
                onClickRow={(row, key) => { navigate(`${parentRoute}/${row.id}`, { state: savedState }) }}
            />
        </DashboardLayout>
    );
}

export default StaffTable;